import React from 'react';
import { Grid, TextField, TextFieldProps, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderPageTypography } from './OrderPageTypography';

export const OrderPageOutlinedTextField = ({
  optional,
  label,
  helperText,
  multiline,
  inputProps,
  ...props
}: TextFieldProps & { optional?: boolean }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { palette } = theme.customTheme;
  const showError = props.error && helperText;
  const errorId = props.id ? `${props.id}-error` : undefined;
  // An LTR value (email, phone, date) in a Hebrew page still lines up with the label at the start edge.
  const textAlign = inputProps?.dir === 'ltr' && i18n.dir() === 'rtl' ? 'end' : 'start';

  return (
    <Grid container flexDirection="column" gap={0.75}>
      <OrderPageTypography variant="body2" style={{ fontWeight: 500 }}>
        <label htmlFor={props.id}>
          {label}
          {optional && (
            <>
              {' '}
              <span style={{ fontWeight: 400, color: palette.lineColor }}>{t('order_page.optional')}</span>
            </>
          )}
        </label>
      </OrderPageTypography>
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        multiline={multiline}
        inputProps={{
          ...inputProps,
          'aria-describedby': showError ? errorId : undefined,
          style: { textAlign, ...inputProps?.style },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: multiline ? 'auto' : 48,
            minHeight: multiline ? 96 : undefined,
            padding: multiline ? 0 : undefined,
            borderRadius: '8px',
            fontSize: 16,
            color: palette.text.primary,
            backgroundColor: palette.background,
            '& fieldset': { borderColor: palette.border },
            '&:hover fieldset': { borderColor: palette.primary },
            '&.Mui-focused fieldset': { borderColor: palette.primary, borderWidth: 1 },
            '&.Mui-error fieldset': { borderColor: palette.error, borderWidth: 2 },
          },
          '& .MuiOutlinedInput-input': { padding: multiline ? '12px' : '0 12px' },
        }}
        {...props}
      />
      {showError && (
        <div id={errorId}>
          <OrderPageTypography color="error" style={{ fontSize: 12 }}>
            {helperText}
          </OrderPageTypography>
        </div>
      )}
    </Grid>
  );
};
